import { createAsyncThunk } from '@reduxjs/toolkit';

import type { RootState } from '@/store';

import type {
  GetHistoryParams,
  InternalChatHistoryResult,
  InternalChatMensagem,
  SendMessageInput,
} from '../../types';
import { isCanalInterno, resolveProcessoArbitralId } from '../../utils/canalInterno';
import {
  selectConversationsItems,
  selectSelectedConversationDetails,
} from '../conversations/selectors';
import { conversationsService } from '../conversations/services';
import { internoChatService } from './internoChatService';
import { messagesService } from './services';

export interface FetchHistoryResult {
  conversaId: string;
  cursorRequested: string | undefined;
  data: InternalChatHistoryResult;
}

const reject = (error: unknown): string =>
  error instanceof Error ? error.message : 'Erro desconhecido';

const resolveInternoContext = (state: RootState, conversaId: string) => {
  const listItem = selectConversationsItems(state).find((item) => item.id === conversaId);
  const selected = selectSelectedConversationDetails(state);
  const details = selected?.id === conversaId ? selected : null;
  const canal = listItem?.canal ?? details?.canal;
  return {
    isInterno: isCanalInterno(canal),
    // O item da lista chega sem `vinculos` enquanto o detalhe não hidrata: cair no detalhe é o
    // que evita o falso 'Conversa Interno sem processo arbitral vinculado' logo após selecionar.
    processoId: resolveProcessoArbitralId(listItem) ?? resolveProcessoArbitralId(details),
    empresaId: state.auth.user?.empresaId ?? '',
  };
};

export const fetchMessageHistory = createAsyncThunk<
  FetchHistoryResult,
  GetHistoryParams,
  { rejectValue: string; state: RootState }
>('chatAtendimento/messages/fetchHistory', async (params, { getState, rejectWithValue }) => {
  try {
    const ctx = resolveInternoContext(getState(), params.conversaId);
    if (ctx.isInterno) {
      if (!ctx.processoId) {
        return rejectWithValue('Conversa Interno sem processo arbitral vinculado');
      }
      const data = await internoChatService.history({
        processoId: ctx.processoId,
        conversaId: params.conversaId,
        empresaId: ctx.empresaId,
        cursor: params.cursor,
        limit: params.limit,
      });
      return { conversaId: params.conversaId, cursorRequested: params.cursor, data };
    }
    const data = await messagesService.history(params);
    return { conversaId: params.conversaId, cursorRequested: params.cursor, data };
  } catch (error) {
    return rejectWithValue(reject(error));
  }
});

export const sendMessage = createAsyncThunk<
  InternalChatMensagem,
  SendMessageInput,
  { rejectValue: string; state: RootState }
>('chatAtendimento/messages/send', async (input, { getState, rejectWithValue }) => {
  try {
    const ctx = resolveInternoContext(getState(), input.conversaId);
    if (ctx.isInterno) {
      if (!ctx.processoId) {
        return rejectWithValue('Conversa Interno sem processo arbitral vinculado');
      }
      return await internoChatService.send({
        processoId: ctx.processoId,
        conversaId: input.conversaId,
        empresaId: ctx.empresaId,
        texto: input.conteudo,
        file: input.file,
      });
    }
    return input.file
      ? await messagesService.sendWithAnexo(input)
      : await messagesService.send(input);
  } catch (error) {
    return rejectWithValue(reject(error));
  }
});

export interface EnrichRealtimeResult {
  conversaId: string;
  items: InternalChatMensagem[];
}

export const enrichRealtimeMessages = createAsyncThunk<
  EnrichRealtimeResult,
  { conversaId: string; limit?: number },
  { rejectValue: string; state: RootState }
>(
  'chatAtendimento/messages/enrichRealtime',
  async ({ conversaId, limit = 10 }, { getState, rejectWithValue }) => {
    try {
      const ctx = resolveInternoContext(getState(), conversaId);
      if (ctx.isInterno) {
        if (!ctx.processoId) return { conversaId, items: [] };
        const data = await internoChatService.history({
          processoId: ctx.processoId,
          conversaId,
          empresaId: ctx.empresaId,
          limit,
        });
        return { conversaId, items: data.items };
      }
      const data = await messagesService.history({ conversaId, limit });
      return { conversaId, items: data.items };
    } catch (error) {
      return rejectWithValue(reject(error));
    }
  },
);

export const resendTemplate = createAsyncThunk<
  InternalChatMensagem,
  string,
  { rejectValue: string }
>('chatAtendimento/messages/resendTemplate', async (mensagemId, { rejectWithValue }) => {
  try {
    return await messagesService.resendTemplate(mensagemId);
  } catch (error) {
    return rejectWithValue(reject(error));
  }
});

export const resumeConversationTemplate = createAsyncThunk<
  InternalChatMensagem,
  string,
  { rejectValue: string }
>(
  'chatAtendimento/messages/resumeConversationTemplate',
  async (conversaId, { rejectWithValue }) => {
    try {
      return await conversationsService.resumeTemplate(conversaId);
    } catch (error) {
      return rejectWithValue(reject(error));
    }
  },
);
